'use client';

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useTotalUnreadCount } from '@kit/chat/hooks';
import { ASSIGNMENT_STATUS_GROUPS } from '@kit/events/constants/assignment-statuses';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import type { Database } from '~/lib/database.types';

export interface NavigationBadgeCounts {
  chatUnread: number;
  pendingAssignments: number;
  pendingFeedback: number;
  reimbursementsNeedingAction: number;
  adminPendingHireOrders: number;
  adminPendingInvoices: number;
  adminPendingReimbursements: number;
  clientPendingInvoices: number;
}

/** Shared query config for all badge count queries */
const BADGE_QUERY_OPTIONS = {
  staleTime: 30_000,
  refetchInterval: 60_000,
} as const;

export function useNavigationBadgeCounts(params: {
  userId: string;
  dancerProfileId: string | null;
  isSuperAdmin?: boolean;
  clientId?: string | null;
}) {
  const client = useSupabase<Database>();
  const { data: chatUnread = 0, isLoading: isChatLoading } =
    useTotalUnreadCount();

  const pendingAssignmentsQuery = useQuery({
    queryKey: ['nav-badges', 'pending-assignments', params.userId],
    queryFn: async () => {
      const { count, error } = await client
        .from('cast_assignments')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', params.userId)
        .in('assignment_status', [...ASSIGNMENT_STATUS_GROUPS.PENDING]);
      return error ? 0 : (count ?? 0);
    },
    enabled: !!params.userId,
    ...BADGE_QUERY_OPTIONS,
  });

  const pendingFeedbackQuery = useQuery({
    queryKey: ['nav-badges', 'pending-feedback', params.dancerProfileId],
    queryFn: async () => {
      if (!params.dancerProfileId) return 0;
      const { count, error } = await client
        .from('feedback_requests')
        .select('*', { count: 'exact', head: true })
        .eq('dancer_id', params.dancerProfileId)
        .eq('status', 'pending');
      return error ? 0 : (count ?? 0);
    },
    enabled: !!params.dancerProfileId,
    ...BADGE_QUERY_OPTIONS,
  });

  const reimbursementsNeedingActionQuery = useQuery({
    queryKey: ['nav-badges', 'reimbursements-needing-action', params.userId],
    queryFn: async () => {
      const { count, error } = await client
        .from('reimbursement_requests')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', params.userId)
        .in('status', ['draft', 'rejected']);
      return error ? 0 : (count ?? 0);
    },
    enabled: !!params.userId,
    ...BADGE_QUERY_OPTIONS,
  });

  const adminPendingHireOrdersQuery = useQuery({
    queryKey: ['nav-badges', 'admin', 'pending-hire-orders'],
    queryFn: async () => {
      const { count, error } = await client
        .from('hire_orders')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'sent');
      return error ? 0 : (count ?? 0);
    },
    enabled: !!params.isSuperAdmin,
    ...BADGE_QUERY_OPTIONS,
  });

  const adminPendingInvoicesQuery = useQuery({
    queryKey: ['nav-badges', 'admin', 'pending-invoices'],
    queryFn: async () => {
      const { count, error } = await client
        .from('invoices')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'issued');
      return error ? 0 : (count ?? 0);
    },
    enabled: !!params.isSuperAdmin,
    ...BADGE_QUERY_OPTIONS,
  });

  const adminPendingReimbursementsQuery = useQuery({
    queryKey: ['nav-badges', 'admin', 'pending-reimbursements'],
    queryFn: async () => {
      const { count, error } = await client
        .from('reimbursement_requests')
        .select('id', { count: 'exact', head: true })
        .in('status', ['pending', 'under_review']);
      return error ? 0 : (count ?? 0);
    },
    enabled: !!params.isSuperAdmin,
    ...BADGE_QUERY_OPTIONS,
  });

  const clientPendingInvoicesQuery = useQuery({
    queryKey: ['nav-badges', 'client', 'pending-invoices', params.clientId],
    queryFn: async () => {
      if (!params.clientId) return 0;
      const { count, error } = await client
        .from('invoices')
        .select('id', { count: 'exact', head: true })
        .eq('client_id', params.clientId)
        .eq('status', 'issued')
        .neq('payment_status', 'paid');
      return error ? 0 : (count ?? 0);
    },
    enabled: !!params.clientId,
    ...BADGE_QUERY_OPTIONS,
  });

  return useMemo(
    () => ({
      counts: {
        chatUnread,
        pendingAssignments: pendingAssignmentsQuery.data ?? 0,
        pendingFeedback: pendingFeedbackQuery.data ?? 0,
        reimbursementsNeedingAction: reimbursementsNeedingActionQuery.data ?? 0,
        adminPendingHireOrders: adminPendingHireOrdersQuery.data ?? 0,
        adminPendingInvoices: adminPendingInvoicesQuery.data ?? 0,
        adminPendingReimbursements: adminPendingReimbursementsQuery.data ?? 0,
        clientPendingInvoices: clientPendingInvoicesQuery.data ?? 0,
      } satisfies NavigationBadgeCounts,
      isLoading:
        isChatLoading ||
        pendingAssignmentsQuery.isLoading ||
        pendingFeedbackQuery.isLoading ||
        reimbursementsNeedingActionQuery.isLoading ||
        adminPendingHireOrdersQuery.isLoading ||
        adminPendingInvoicesQuery.isLoading ||
        adminPendingReimbursementsQuery.isLoading ||
        clientPendingInvoicesQuery.isLoading,
    }),
    [
      chatUnread,
      isChatLoading,
      pendingAssignmentsQuery.data,
      pendingAssignmentsQuery.isLoading,
      pendingFeedbackQuery.data,
      pendingFeedbackQuery.isLoading,
      reimbursementsNeedingActionQuery.data,
      reimbursementsNeedingActionQuery.isLoading,
      adminPendingHireOrdersQuery.data,
      adminPendingHireOrdersQuery.isLoading,
      adminPendingInvoicesQuery.data,
      adminPendingInvoicesQuery.isLoading,
      adminPendingReimbursementsQuery.data,
      adminPendingReimbursementsQuery.isLoading,
      clientPendingInvoicesQuery.data,
      clientPendingInvoicesQuery.isLoading,
    ],
  );
}
